import type { LDClient, LDUser, LDOptions } from 'launchdarkly-js-client-sdk'
import { initialize } from 'launchdarkly-js-client-sdk'

export const defaultLDOptions = { useReport: true, sendEvents: false }
let ldClient: LDClient | undefined

const init = async (clientId: string, ldUser: LDUser, options?: LDOptions): Promise<LDClient> => {
  const newLdClient = initialize(clientId, ldUser, options)
  await newLdClient.waitForInitialization()

  // Cache the client for subsequent requests
  ldClient = newLdClient

  return ldClient
}

export const launchDarklyClient = async (
  clientId: string,
  ldUser?: LDUser,
  options: LDOptions = defaultLDOptions,
): Promise<LDClient> => {
  const user = { ...ldUser, anonymous: true }

  // If we already have a client we just update the user and return the existing client
  if (ldClient) {
    await ldClient.identify(user, undefined)
    return ldClient
  }

  // If we don't have a client we create a new one and wait for it to be ready
  const newLdClient = await init(clientId, user, { ...defaultLDOptions, ...options })
  await newLdClient.waitUntilReady()

  return newLdClient
}

export const close = async () => {
  if (ldClient) {
    const result = ldClient.close()
    ldClient = undefined as never
    return await result
  }
}

/**
 * Retrieves all the flags for the given user from LaunchDarkly.
 * @type T The type for the set of flags returned
 * @param clientId The client ID for the project's account in LaunchDarkly.
 * @param ldUser The user to retrieve.
 * @param options
 */
export const getAllLaunchDarklyFlags = async <T>(clientId: string, ldUser?: LDUser, options?: LDOptions) => {
  const client = await launchDarklyClient(clientId, ldUser, options)
  return client.allFlags() as T
}
